import { useState, useMemo, useEffect } from 'react';
import {
  Stack,
  Button,
  FormSelect,
  FormField,
  FormInputText,
  Tooltip,
  TextArea,
} from '@fashionnovateam/nova-ui-toolkit';
import Page from '../Page';
import { useSimulateNsRequestToPoService } from './hooks/dev-tool.hooks';
import Header from '../Header';

interface Request {
  name: string;
  method: string;
  body: any;
  url: string;
  tooltip?: React.ReactNode;
}

function DevTools() {
  const simulateNsRequestToPoService = useSimulateNsRequestToPoService();
  const [method, setMethod] = useState('post');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('');
  const [poNumber, setPoNumber] = useState('<PO Number>');
  const [selectNsRequest, setSelectNsRequest] = useState<number | null>(null);
  const [rawJson, setRawJson] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  const nsRequest: Array<Request> = useMemo(
    () => [
      {
        name: 'NS PO accepted handcheck',
        method: 'patch',
        body: {
          identifier: 'netsuite@example.com',
          full_name: 'Netsuite User',
        },
        url: `/purchase-orders/${poNumber}/ns/accept`,
        tooltip: (
          <>
            After a PO is accepted by a Vendor, <br />
            NS will send a request to PO Service to update <br />
            the purchase_order.is_accepted field to true
          </>
        ),
      },
      {
        name: 'NS update TOP Status at the PO Header level',
        method: 'patch',
        body: {
          top_status: 'Received',
          author: {
            full_name: 'Netsuite User',
            identifier: 'netsuite@example.com',
            type: 'internal',
          },
          purchase_order_lines: [
            {
              line_id: '1',
              top_status: 'Received',
            },
          ],
        },
        url: `/purchase-orders/${poNumber}/ns/top-status`,
      },
      {
        name: 'NS mark PO received',
        method: 'patch',
        body: {
          status: 'Pending Bill',
          received_date: '2024-01-15T00:00:00.000Z',
          purchase_order_lines: [
            {
              line_id: 1,
              wms_id: 1,
              quantity_received: 100,
              total: 100,
            },
          ],
          is_accepted: false,
          po_last_modified_date_time: '2024-01-15T00:00:00.000Z',
          creation_date: '2024-01-10T00:00:00.000Z',
          purchase_type: 'New',
        },
        url: `/purchase-orders/${poNumber}/ns/delivery-received`,
      },
      {
        name: 'NS mark PO bill paid',
        method: 'put',
        body: null,
        url: `/purchase-orders/${poNumber}/ns/bill-paid`,
      },
      {
        name: 'NS cancel PO',
        method: 'patch',
        body: null,
        url: `/purchase-orders/${poNumber}/ns/cancel`,
      },
      {
        name: 'NS PO Revision approved',
        method: 'post',
        body: null,
        url: `/purchase-order-revision/${poNumber}/ns-approved`,
        tooltip: (
          <>
            After a PO Revision is accepted in Helix, <br />
            NS will be notified to confirm the approval <br />
            sets PO Revision status to REVISION_READY_FOR_NS_UPDATE or
            REVISION_COMPLETED
          </>
        ),
      },
      {
        name: 'NS PO Revision cancelled',
        method: 'post',
        body: null,
        url: `/purchase-order-revision/${poNumber}/ns-cancelled`,
        tooltip: (
          <>
            After a PO Revision is cancelled in Helix, <br />
            NS will be notified to confirm the cancelation <br />
            sets PO Revision status to REVISION_CANCELLED_BY_NS
          </>
        ),
      },
    ],
    [poNumber],
  );

  useEffect(() => {
    if (selectNsRequest !== null) {
      setMethod(nsRequest[selectNsRequest].method);
      setBody(nsRequest[selectNsRequest].body);
      setRawJson(JSON.stringify(nsRequest[selectNsRequest].body, null, 2));
      setUrl(nsRequest[selectNsRequest].url);
    }
  }, [selectNsRequest, nsRequest]);

  const handleJsonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setRawJson(newValue);

    try {
      const parsed = JSON.parse(newValue);
      setBody(parsed);
      setError(undefined);
    } catch (err) {
      setError('Invalid JSON format');
    }
  };

  return (
    <Page.Container>
      <Header.Major icon="file/file-settings" title="Dev Tools" />
      <Page.Content>
        <Stack direction="row" content="start" gap="xl" className="mb-8">
          {nsRequest.map((request, index) => (
            <Tooltip key={request.name} content={request.tooltip}>
              <Button onClick={() => setSelectNsRequest(index)}>
                {request.name}
              </Button>
            </Tooltip>
          ))}
        </Stack>
        <Stack direction="col">
          <FormInputText
            label="PO Number"
            value={poNumber}
            className="mb-4"
            onBlur={(e) => setPoNumber(e.target.value)}
            onChange={(e) => setPoNumber(e.target.value)}
          />
          <FormInputText
            label="Url"
            value={url}
            className="mb-4"
            onBlur={(e) => setUrl(e.target.value)}
            onChange={(e) => setUrl(e.target.value)}
          />
          <FormField label="Body" inputId="body" className="mb-4" error={error}>
            <TextArea
              onChange={handleJsonChange}
              rows={20}
              cols={50}
              value={rawJson || JSON.stringify(body, null, 2)} // Keep JSON formatted
              style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }} // Preserve formatting
            />
          </FormField>
          <FormSelect
            label="Method"
            inputId="method"
            name="method"
            className="mb-4"
            placeholder="Select an HTTP method"
            options={[
              { value: 'post', label: 'Post' },
              { value: 'put', label: 'Put' },
              { value: 'patch', label: 'Patch' },
              { value: 'get', label: 'Get' },
            ]}
            required
            value={method}
            onChangeForm={(_, value) => setMethod(value)}
            disabled={simulateNsRequestToPoService.isLoading}
          />
          <Button
            onClick={() => {
              simulateNsRequestToPoService.mutate({
                method,
                url,
                body,
              });
            }}
          >
            Simulate NS Request to PO Service
          </Button>
        </Stack>
      </Page.Content>
    </Page.Container>
  );
}

export default DevTools;
